// Fine-grained, focus-preserving bindings — the piece live-edit forms need that
// the coarse mount() can't give. Instead of re-rendering a subtree on every
// keystroke (which destroys the focused input + caret), these update a single
// node in place via an effect. All styling stays CSSOM (CSP-safe).
import { effect, type Signal } from './signal';

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

// Two-way bind a form field to a string signal WITHOUT recreating the node.
// Reads on `event`; writes back only when the value actually differs, so the
// caret never jumps when an unrelated derived value recomputes.
export function bindInput(el: Field, sig: Signal<string>, event: 'input' | 'change' = 'input'): void {
	effect(() => {
		const v = sig();
		if (el.value !== v) el.value = v;
	});
	el.addEventListener(event, () => sig.set(el.value));
}

// Two-way bind a checkbox to a boolean signal.
export function bindChecked(el: HTMLInputElement, sig: Signal<boolean>): void {
	effect(() => {
		if (el.checked !== sig()) el.checked = sig();
	});
	el.addEventListener('change', () => sig.set(el.checked));
}

// A Text node whose content tracks a getter — lets a derived total update in
// place next to a focused input without touching the input.
export function text(getter: () => string | number): Text {
	const node = document.createTextNode('');
	effect(() => {
		node.data = String(getter());
	});
	return node;
}

// Reactively toggle a class on an element in place.
export function toggleClass(el: Element, name: string, on: () => boolean): void {
	effect(() => el.classList.toggle(name, !!on()));
}

// Reactively set a CSSOM style property in place (CSP-safe — never an attribute).
export function styleProp(el: HTMLElement, prop: string, getter: () => string): void {
	effect(() => el.style.setProperty(prop, getter()));
}
